"use client";

import React from "react";
import { Shield, Key, Plug, Zap, Layers3, Building2 } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = ["Customers provide their own LLM and OCR API keys, so model usage stays under their direct control instead of being absorbed into the platform.", "EloRAG keeps platform pricing separate from variable AI usage, which makes RAG deployment easier to forecast across teams, tenants, and clients.", "Agencies can deliver branded client environments without taking on hidden model consumption through platform-owned fallback keys.", "BYO-Key keeps the commercial model cleaner: EloRAG provides the retrieval and governance layer, while usage is billed by the chosen providers."];
export default function PredictableAiCostsWithByoKey() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="predictable-ai-costs-with-byo-key" className="overflow-hidden bg-muted/20 px-6 py-16 text-foreground md:px-12 lg:py-20">
      <div ref={ref} className={`mx-auto max-w-6xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            <span>Deployable RAG Economics</span>
          </div>

          <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">
            Predictable RAG economics with BYO-Key
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground">
            EloRAG gives businesses and agencies the retrieval and governance layer, while customers
            keep direct control over LLM and OCR usage through their own provider keys.
          </p>
        </div>

        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="relative flex min-h-[300px] flex-col justify-center rounded-2xl border border-border/70 bg-card p-8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.16)] md:p-12">
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-transparent" />

            <div className="relative mx-auto flex w-full max-w-md items-center justify-between">
              <div className="z-10 flex w-28 flex-col items-center gap-4">
                <div className="group relative flex h-16 w-16 items-center justify-center rounded-xl border border-border bg-secondary shadow-inner">
                  <div className="absolute inset-0 rounded-xl bg-primary/10 opacity-0 transition-opacity group-hover:opacity-100" />
                  <Key className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">Customer Keys</p>
                  <p className="mt-1 text-xs text-muted-foreground">LLM and OCR providers</p>
                </div>
              </div>

              <div className="relative z-0 flex flex-1 items-center justify-center px-2">
                <div className="absolute h-[2px] w-full bg-border">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
                </div>
                <div className="relative z-10 rounded-full border border-border bg-background p-2.5 shadow-sm">
                  <div className="absolute inset-0 animate-pulse rounded-full bg-primary/10" />
                  <Plug className="relative z-10 h-5 w-5 text-muted-foreground" />
                </div>
              </div>

              <div className="z-10 flex w-28 flex-col items-center gap-4">
                <div className="relative flex h-16 w-16 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 shadow-[0_0_20px_rgba(var(--color-primary),0.15)]">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">EloRAG</p>
                  <p className="mt-1 text-xs text-muted-foreground">RAG + governance layer</p>
                </div>
              </div>
            </div>

            <div className="relative mt-10 flex items-center justify-center gap-3 rounded-xl border border-border/60 bg-background/80 px-4 py-3 text-sm text-muted-foreground">
              <Building2 className="size-4 text-primary" />
              <span>Scales across business teams, tenants, and client environments</span>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <ul className="mb-8 space-y-5">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                  <div className="mt-1 shrink-0 rounded-md bg-primary/10 p-1">
                    <Shield className="h-4 w-4 text-primary" />
                  </div>
                  <span className="leading-relaxed text-muted-foreground">{benefit}</span>
                </li>)}
            </ul>

            <Alert className="border border-primary/20 bg-primary/5 shadow-[0_10px_25px_-18px_rgba(0,0,0,0.14)]">
              <Zap className="h-5 w-5 text-primary" />
              <AlertTitle className="font-semibold tracking-wide text-foreground">
                Clear separation between platform and usage
              </AlertTitle>
              <AlertDescription className="mt-1.5 text-muted-foreground">
                EloRAG charges for the platform layer. LLM and OCR usage are billed directly by the
                customer’s own providers, which makes scaling cleaner for both businesses and agencies.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </section>;
}